import React, { useEffect } from 'react';
import Dropdown from '../../ui/Dropdown';
import SegmentedControl from '../../ui/SegmentedControl';
import { INDEX_OPTIONS, EXPIRY_TYPE_OPTIONS, EXPIRY_TYPE } from '../../../utils/strategy/strategyConstants';
import { ExpiryType, ProductType, TradingType, IntradayExitMode } from '../../../types/strategy';
import { MONTHLY_ONLY_UNDERLYINGS, PRODUCT_TYPE_OPTIONS } from '../../../constants/tradingConstants';
import { isMISAllowed, autoCorrectProductType } from '../../../utils/strategy/productTypeValidation';

interface GlobalIndexSelectorProps {
  value: string;
  onChange: (value: string) => void;
  expiryType: ExpiryType;
  onExpiryTypeChange: (expiryType: ExpiryType) => void;
  productType: ProductType;
  onProductTypeChange: (productType: ProductType) => void;
  tradingType: TradingType;
  intradayExitMode: IntradayExitMode;
}

const GlobalIndexSelector: React.FC<GlobalIndexSelectorProps> = ({
  value,
  onChange,
  expiryType,
  onExpiryTypeChange,
  productType,
  onProductTypeChange,
  tradingType,
  intradayExitMode
}) => {
  const isMonthlyOnly = MONTHLY_ONLY_UNDERLYINGS.includes(value);

  // Allowed expiry types for monthly-only underlyings: current monthly + next monthly.
  const isMonthlyExpiry = (t: ExpiryType) =>
    t === EXPIRY_TYPE.MONTHLY || t === EXPIRY_TYPE.NEXT_MONTHLY;

  const handleIndexChange = (newIndex: string) => {
    onChange(newIndex);
    // Auto-correct expiry type for monthly-only underlyings — fall back to MONTHLY
    // when the previous selection was a weekly variant.
    if (MONTHLY_ONLY_UNDERLYINGS.includes(newIndex) && !isMonthlyExpiry(expiryType)) {
      onExpiryTypeChange(EXPIRY_TYPE.MONTHLY as ExpiryType);
    }
  };

  const handleExpiryTypeChange = (next: string) => {
    onExpiryTypeChange(next as ExpiryType);
  };

  // Filter expiry options: monthly-only underlyings only show monthly + next_monthly
  const filteredExpiryOptions = isMonthlyOnly
    ? EXPIRY_TYPE_OPTIONS.filter(o => o.value === EXPIRY_TYPE.MONTHLY || o.value === EXPIRY_TYPE.NEXT_MONTHLY)
    : EXPIRY_TYPE_OPTIONS;

  // Auto-correct expiry type on mount/underlying change for monthly-only underlyings
  useEffect(() => {
    if (isMonthlyOnly && !isMonthlyExpiry(expiryType)) {
      onExpiryTypeChange(EXPIRY_TYPE.MONTHLY as ExpiryType);
    }
  }, [value, isMonthlyOnly, expiryType, onExpiryTypeChange]);

  // Check if MIS is allowed based on current trading configuration
  const misAllowed = isMISAllowed(tradingType, intradayExitMode);

  // Auto-correct product type when trading configuration changes
  // Only auto-corrects when MIS is selected but not allowed
  useEffect(() => {
    const { productType: correctedProductType, wasChanged } = autoCorrectProductType(
      productType,
      tradingType,
      intradayExitMode
    );

    if (wasChanged) {
      onProductTypeChange(correctedProductType);
    }
  }, [tradingType, intradayExitMode, productType, onProductTypeChange]);

  const handleProductTypeChange = (newProductType: string) => {
    // Only allow the change if it's valid
    if (newProductType === 'MIS' && !misAllowed) {
      return;
    }
    onProductTypeChange(newProductType as ProductType);
  };

  const productTypeOptions = PRODUCT_TYPE_OPTIONS.map(o => ({
    ...o,
    disabled: o.value === 'MIS' && !misAllowed,
    title: o.value === 'MIS' && !misAllowed
      ? 'MIS is only available for INTRADAY trading with SAME_DAY exit mode'
      : undefined,
  }));

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Index</label>
          <Dropdown value={value} onChange={handleIndexChange} options={INDEX_OPTIONS} />
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Expiry Type</label>
          <Dropdown value={expiryType} onChange={handleExpiryTypeChange} options={filteredExpiryOptions} />
          {isMonthlyOnly && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {value} supports monthly and next monthly expiry only
            </p>
          )}
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Product Type</label>
          <SegmentedControl value={productType} onChange={handleProductTypeChange} options={productTypeOptions} fullWidth />
          {!misAllowed && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              MIS requires INTRADAY + SAME_DAY exit mode
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default GlobalIndexSelector;
